/**
 * API Client
 * 
 * Centralized axios instance with JWT authentication interceptors.
 * Handles automatic token injection, token refresh, and error handling.
 * 
 * This replaces the old axios-pos.js and axios-pos-v2.js files.
 */

import axios from 'axios';
import API_CONFIG, { API_ENDPOINTS, HTTP_STATUS } from '../config/api.config';
import {
  getAccessToken,
  getRefreshToken,
  saveTokens,
  clearTokens,
  isTokenExpired,
} from '../utils/tokenManager';
import { logError } from '../utils/errorHandler';

/**
 * Create axios instance with base configuration
 */
const apiClient = axios.create(API_CONFIG);

/**
 * Flag to prevent multiple simultaneous token refresh requests
 */
let isRefreshing = false;
let refreshSubscribers = [];

/**
 * Subscribe to token refresh completion
 * @param {Function} callback - Callback to execute after token refresh
 */
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

/**
 * Notify all subscribers that token refresh is complete
 * @param {string} token - New access token
 */
const onTokenRefreshed = (token) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

/**
 * Request Interceptor
 * Adds JWT token to all requests
 */
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    
    // Add Bearer token to Authorization header if available
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    
    return config;
  },
  (error) => {
    logError(error, 'Request Interceptor');
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles token refresh on 401 errors and logs responses
 */
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Log error in development — skip expected 401 on auth check (handled gracefully)
    const isExpectedAuthCheck401 =
      error.response?.status === 401 &&
      originalRequest.url.includes(API_ENDPOINTS.AUTH_ME);
    if (!isExpectedAuthCheck401) {
      logError(error, 'Response Interceptor');
    }
    
    // If error is 401 and we haven't already tried to refresh
    if (
      error.response &&
      error.response.status === HTTP_STATUS.UNAUTHORIZED &&
      !originalRequest._retry
    ) {
      // Skip token refresh for login and refresh endpoints
      if (
        originalRequest.url.includes(API_ENDPOINTS.AUTH_LOGIN) ||
        originalRequest.url.includes(API_ENDPOINTS.AUTH_REFRESH)
      ) {
        return Promise.reject(error);
      }
      
      // Mark this request as retried
      originalRequest._retry = true;
      
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(apiClient(originalRequest));
          });
        });
      }
      
      // Start token refresh process
      isRefreshing = true;
      
      try {
        const refreshToken = getRefreshToken();
        
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }
        
        // Make refresh token request
        const response = await axios.post(
          `${API_CONFIG.baseURL}${API_ENDPOINTS.AUTH_REFRESH}`,
          { refreshToken },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
        
        const { accessToken, refreshToken: newRefreshToken } = response.data;
        
        // Save new tokens
        saveTokens({
          accessToken,
          refreshToken: newRefreshToken,
        });
        
        // Update the original request with new token
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        
        // Notify all queued requests
        onTokenRefreshed(accessToken);
        
        // Reset refreshing flag
        isRefreshing = false;
        
        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Token refresh failed, clear tokens and redirect to login
        isRefreshing = false;
        refreshSubscribers = [];
        clearTokens();
        
        // Redirect to login page
        window.location.href = '/login';
        
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * Helper function to make GET requests
 * @param {string} url - Request URL
 * @param {Object} config - Axios config
 * @returns {Promise} Axios response promise
 */
export const get = (url, config = {}) => {
  return apiClient.get(url, config);
};

/**
 * Helper function to make POST requests
 * @param {string} url - Request URL
 * @param {Object} data - Request data
 * @param {Object} config - Axios config
 * @returns {Promise} Axios response promise
 */
export const post = (url, data, config = {}) => {
  return apiClient.post(url, data, config);
};

/**
 * Helper function to make PUT requests
 * @param {string} url - Request URL
 * @param {Object} data - Request data
 * @param {Object} config - Axios config
 * @returns {Promise} Axios response promise
 */
export const put = (url, data, config = {}) => {
  return apiClient.put(url, data, config);
};

/**
 * Helper function to make PATCH requests
 * @param {string} url - Request URL
 * @param {Object} data - Request data
 * @param {Object} config - Axios config
 * @returns {Promise} Axios response promise
 */
export const patch = (url, data, config = {}) => {
  return apiClient.patch(url, data, config);
};

/**
 * Helper function to make DELETE requests
 * @param {string} url - Request URL
 * @param {Object} config - Axios config
 * @returns {Promise} Axios response promise
 */
export const del = (url, config = {}) => {
  return apiClient.delete(url, config);
};

/**
 * Export the main axios instance for direct use if needed
 */
export default apiClient;
